import React, {useCallback, useState} from 'react';
import Button from '../../../components/common/button/Button';
import Input from '../../../components/common/input/Input';
import Modal from 'react-modal';
import {changePasswordAPI} from '../../../services/auth';
import styles from './ChangePasswordPopUp.module.css';
import {useAlert} from "../../../components/common/error/AlertContext";

interface PasswordData {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface ChangePasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPasswordChangeSuccess?: () => void;
    isFirstLogin?: boolean;
}
//클라이언트 비밀번호 변경 팝업
const ChangePasswordPopUp: React.FC<ChangePasswordModalProps> = ({
                                                                     isOpen,
                                                                     onClose,
                                                                     onPasswordChangeSuccess,
                                                                     isFirstLogin
                                                                 }) => {
    const [passwordData, setPasswordData] = useState<PasswordData>({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const {showAlert} = useAlert();

    const [errors, setErrors] = useState<{
        currentPassword?: string;
        newPassword?: string;
        confirmPassword?: string;
    }>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    //입력
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
        const {name, value} = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value.trim()
        }));
        setErrors(prev => ({
            ...prev,
            [name]: undefined
        }));
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isLoading) return;

        setErrors({});

        const passwordError = validatePassword(passwordData.newPassword);
        if (passwordError) {
            //조건 미달 시 에러
            showAlert(passwordError, "error");
            return;
        }

        const newErrors: Partial<PasswordData> = {};
        //입력 확인
        if (!passwordData.currentPassword) {
            newErrors.currentPassword = '현재 비밀번호를 입력하세요';
        }
        if (!passwordData.newPassword) {
            newErrors.newPassword = '새 비밀번호를 입력하세요';
        }
        if (!passwordData.confirmPassword) {
            newErrors.confirmPassword = '비밀번호 확인을 입력하세요';
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            newErrors.confirmPassword = '비밀번호 확인이 일치하지 않습니다';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);

        try {
            //비밀번호 변경 API
            await changePasswordAPI(passwordData.currentPassword, passwordData.newPassword);
            if (onPasswordChangeSuccess) {
                onPasswordChangeSuccess();
            }
        } catch (error) {
            console.error('Password change failed:', error);
            setPasswordData(prev => ({
                ...prev,
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            }));
        } finally {
            setIsLoading(false);
        }
    };

    //비밀번호 조건 확인
    const validatePassword = (password: string) => {
        //최소 8자
        const minLength = 8;
        //문자 사용
        const hasLetter = /[a-zA-Z]/.test(password);
        //숫자 사용
        const hasNumber = /[0-9]/.test(password);
        //특수기호 사용
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        //에러 메세지
        if (password.length < minLength) {
            return '비밀번호는 8자 이상이어야 합니다.';
        }
        if (!hasLetter) {
            return '비밀번호에 문자가 포함되어야 합니다.';
        }
        if (!hasNumber) {
            return '비밀번호에 숫자가 포함되어야 합니다.';
        }
        if (!hasSpecial) {
            return '비밀번호에 특수문자가 포함되어야 합니다.';
        }
        return null;
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            shouldCloseOnOverlayClick={!isFirstLogin}
            shouldCloseOnEsc={!isFirstLogin}
            className={styles.modal}
            overlayClassName={styles.overlay}
        >
            <div className={styles.modalContent}>
                <div className={styles.header}>
                    <h2>비밀번호 변경</h2>
                </div>

                <form onSubmit={handleSubmit} noValidate
                      className={styles.inputs}>
                    {/*현재 비밀번호 입력창*/}
                    <Input
                        label="현재 비밀번호"
                        type="password"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handleChange}
                        placeholder="현재 비밀번호 입력"
                        fullWidth
                        required
                        disabled={isLoading}
                        error={errors.currentPassword}
                        style={{paddingRight: '0px'}}
                    />
                    {/*새 비밀번호 입력창*/}
                    <Input
                        label="새 비밀번호"
                        type="password"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handleChange}
                        placeholder="새 비밀번호 입력"
                        fullWidth
                        required
                        disabled={isLoading}
                        error={errors.newPassword}
                        style={{paddingRight: '0px'}}
                    />
                    {/*새 비밀번호 확인 입력창*/}
                    <Input
                        label="새 비밀번호 확인"
                        type="password"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handleChange}
                        placeholder="새 비밀번호 확인"
                        fullWidth
                        required
                        disabled={isLoading}
                        error={errors.confirmPassword}
                        style={{paddingRight: '0px'}}
                    />
                    {/*비밀번호 변경 버튼*/}
                    <Button
                        type="submit"
                        variant="primary"
                        size='large'
                        fullWidth
                        disabled={isLoading}
                        className={styles.submitButton}
                    >
                        {isLoading ? '변경 중...' : '변경하기'}
                    </Button>
                </form>
            </div>
        </Modal>
    );
};

export default ChangePasswordPopUp;